import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { fetchExpertReportBlocks } from './api';
import { expertReportKeys } from './hooks';
import type { ExpertReportBlockTreeNode } from './types';

/** Плоский элемент блока: id + name (для подписей колонок) */
export type ExpertReportBlockInfo = { id: number; name: string };

/** Соответствие reportId → (blockId → block) */
export type ExpertReportBlocksMap = Map<number, Map<number, ExpertReportBlockInfo>>;

/** Рекурсивно обходит дерево блоков и складывает все узлы в плоскую Map */
const flattenBlocksToMap = (
  blocks: ExpertReportBlockTreeNode[],
  acc: Map<number, ExpertReportBlockInfo>,
): Map<number, ExpertReportBlockInfo> => {
  for (const block of blocks) {
    acc.set(block.id, { id: block.id, name: block.name });
    if (block.children?.length) {
      flattenBlocksToMap(block.children, acc);
    }
  }
  return acc;
};

/**
 * Загружает деревья блоков для указанных экспертных заключений и возвращает плоскую Map
 * `reportId → (blockId → {id, name})` — используется для подписей колонок блоков в таблице диалогов.
 */
export const useExpertReportsBlocksMap = (reportIds: number[]): ExpertReportBlocksMap => {
  const queries = useQueries({
    queries: reportIds.map((id) => ({
      queryKey: expertReportKeys.blocks(id),
      queryFn: () => fetchExpertReportBlocks(id),
      staleTime: 60_000,
    })),
  });

  return useMemo(() => {
    const map: ExpertReportBlocksMap = new Map();
    queries.forEach((q, index) => {
      const reportId = reportIds[index];
      const blocks = q.data?.blocks ?? [];
      const flat = flattenBlocksToMap(blocks, new Map());
      map.set(reportId, flat);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.map((q) => q.dataUpdatedAt).join('|'), reportIds.join(',')]);
};
